import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsCarousel() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/reviews?limit=10`)
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => setReviews([]));
  }, []);

  const total = reviews.length;
  useEffect(() => {
    if (total < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 7000);
    return () => clearInterval(t);
  }, [total]);

  if (!total) return null;

  const active = reviews[idx];
  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const stars = active.rating || 5;

  return (
    <section
      data-testid="reviews"
      className="relative bg-[color:var(--sr-bg)] py-24 md:py-40 border-t hairline-light"
    >
      <div className="sr-container">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> In their words
            </p>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight text-[color:var(--sr-primary)]">
              A quiet chorus of <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">devoted drinkers.</em>
            </h2>
          </div>
          {total > 1 && (
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={prev}
                data-testid="review-prev"
                className="p-3 border hairline hover:border-[color:var(--sr-primary)] transition-colors text-[color:var(--sr-primary)]"
                aria-label="Previous review"
              >
                <ArrowLeft size={16} strokeWidth={1.25} />
              </button>
              <button
                onClick={next}
                data-testid="review-next"
                className="p-3 border hairline hover:border-[color:var(--sr-primary)] transition-colors text-[color:var(--sr-primary)]"
                aria-label="Next review"
              >
                <ArrowRight size={16} strokeWidth={1.25} />
              </button>
            </div>
          )}
        </div>

        <div className="relative min-h-[220px] md:min-h-[280px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id || idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-12 gap-6 items-start"
              data-testid={`review-${idx}`}
            >
              <div className="col-span-12 md:col-span-2 flex items-center gap-1 pt-4 text-[color:var(--sr-accent)]">
                {Array.from({ length: stars }).map((_, i) => (
                  <Star key={i} size={12} strokeWidth={0} fill="currentColor" />
                ))}
              </div>
              <div className="col-span-12 md:col-span-10">
                <p className="font-serif italic text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[color:var(--sr-primary)]">
                  &ldquo;{active.body || active.quote}&rdquo;
                </p>
                <p className="mt-8 label-eyebrow text-[color:var(--sr-text)]/70">
                  {active.name || active.author}
                  {(active.location || active.place) && ` — ${active.location || active.place}`}
                  {active.verified_purchase && (
                    <span className="ml-3 text-[color:var(--sr-accent)]">· Verified purchase</span>
                  )}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {total > 1 && (
          <div className="mt-12 flex items-center gap-2">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`review-dot-${i}`}
                className="p-3 min-h-[44px] flex items-center"
                aria-label={`Go to review ${i + 1}`}
              >
                <span
                  className={`block h-px transition-all ${
                    i === idx
                      ? "w-16 bg-[color:var(--sr-primary)]"
                      : "w-8 bg-[color:var(--sr-primary)]/25"
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
